import axios from 'axios';
import { toast } from 'sonner';
import { useStageStore } from './stores/stage-store';
import { useEditorStore } from './stores/editor-store';
import { exportLayoutJson } from '@/export';

/**
 * Unified save: PUTs to the HitFactor.pro API when a `stageId` is set
 * (embedded mode), otherwise downloads the layout as JSON so standalone
 * users still have a way to persist their work. Surfaces success / failure
 * via sonner.
 */
export async function smartSave(): Promise<void> {
  const { layout, stageId, saveToServer } = useStageStore.getState();
  if (!layout) {
    toast.error('Nothing to save — load or create a stage first');
    return;
  }

  if (!stageId) {
    const filename = `${(layout.metadata.name || 'stage')
      .toLowerCase()
      .replace(/\s+/g, '-')}.json`;
    try {
      exportLayoutJson(layout, filename);
      toast.success(`Saved ${filename}`);
      useEditorStore.getState().setDirty(false);
    } catch (error) {
      console.error('Local save failed:', error);
      toast.error('Failed to download stage file');
    }
    return;
  }

  try {
    await saveToServer();
    toast.success('Stage saved');
    useEditorStore.getState().setDirty(false);
  } catch (error) {
    console.error('Save failed:', error);
    const message =
      axios.isAxiosError(error) && error.response?.status === 409
        ? 'Someone else edited this stage. Reload to see their changes.'
        : error instanceof Error
        ? error.message
        : 'Save failed';
    toast.error(message);
  }
}
